/**
 * External Data API resource
 * Endpoint: /api/easyblocks/external
 */

import { getApiBaseUrl, joinUrl, readJsonSafely } from "./client";
import type {
  ExternalSearchParams,
  ExternalSearchResponse,
  ExternalListParams,
  ExternalListResponse,
  ExternalGetParams,
  ExternalGetResponse,
  ExternalResidentProfile,
  ExternalResolveParams,
  ExternalResolveResponse,
  ExternalAlbum,
  ExternalAlbumPhoto,
  ExternalRosterSegment,
} from "./types";

/**
 * GET /api/easyblocks/external?action=search&source={source}&q={q}&limit={limit}
 * Search external items
 */
export async function searchExternalItems(params: ExternalSearchParams): Promise<ExternalSearchResponse> {
  const url = new URL(joinUrl(getApiBaseUrl(), "external"), window.location.origin);
  url.searchParams.set("action", "search");
  url.searchParams.set("source", params.source);
  url.searchParams.set("q", params.q);
  url.searchParams.set("limit", String(params.limit ?? 50));
  if (params.document_id) url.searchParams.set("document_id", String(params.document_id));
  if (params.include_global_albums) url.searchParams.set("include_global_albums", "1");

  const res = await fetch(url.toString(), {
    method: "GET",
    headers: { Accept: "application/json" },
    credentials: "same-origin",
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`External search failed (${res.status} ${res.statusText}): ${text}`);
  }

  const parsed = (await readJsonSafely(res)) as any;
  if (!parsed || typeof parsed !== "object") {
    throw new Error("External search returned non-JSON response");
  }
  return parsed as ExternalSearchResponse;
}

/**
 * GET /api/easyblocks/external?action=list&source={source}&limit={limit}
 * List external items without text filtering.
 */
export async function listExternalItems(params: ExternalListParams): Promise<ExternalListResponse> {
  const url = new URL(joinUrl(getApiBaseUrl(), "external"), window.location.origin);
  url.searchParams.set("action", "list");
  url.searchParams.set("source", params.source);
  url.searchParams.set("limit", String(params.limit ?? 200));
  if (params.document_id) url.searchParams.set("document_id", String(params.document_id));
  if (params.include_global_albums) url.searchParams.set("include_global_albums", "1");

  const res = await fetch(url.toString(), {
    method: "GET",
    headers: { Accept: "application/json" },
    credentials: "same-origin",
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`External list failed (${res.status} ${res.statusText}): ${text}`);
  }

  const parsed = (await readJsonSafely(res)) as any;
  if (!parsed || typeof parsed !== "object") {
    throw new Error("External list returned non-JSON response");
  }
  return parsed as ExternalListResponse;
}

/**
 * GET /api/easyblocks/external?action=get&source={source}&id={id}
 * Get a single external item
 */
export async function getExternalItem(params: ExternalGetParams): Promise<ExternalGetResponse> {
  const url = new URL(joinUrl(getApiBaseUrl(), "external"), window.location.origin);
  url.searchParams.set("action", "get");
  url.searchParams.set("source", params.source);
  url.searchParams.set("id", params.id);
  if (params.document_id) url.searchParams.set("document_id", String(params.document_id));
  if (params.include_global_albums) url.searchParams.set("include_global_albums", "1");

  const res = await fetch(url.toString(), {
    method: "GET",
    headers: { Accept: "application/json" },
    credentials: "same-origin",
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`External get failed (${res.status} ${res.statusText}): ${text}`);
  }

  const parsed = (await readJsonSafely(res)) as any;
  if (!parsed || typeof parsed !== "object") {
    throw new Error("External get returned non-JSON response");
  }
  return parsed as ExternalGetResponse;
}

/**
 * POST /api/easyblocks/external?action=resolve
 * Resolve multiple external references
 */
export async function resolveExternalData(params: ExternalResolveParams): Promise<ExternalResolveResponse> {
  const url = new URL(joinUrl(getApiBaseUrl(), "external"), window.location.origin);
  url.searchParams.set("action", "resolve");

  const body: any = { externals: params.externals };
  if (params.document_id) body.document_id = params.document_id;
  if (params.include_global_albums) body.include_global_albums = true;

  const res = await fetch(url.toString(), {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    credentials: "same-origin",
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`External resolve failed (${res.status} ${res.statusText}): ${text}`);
  }

  const parsed = (await readJsonSafely(res)) as any;
  if (!parsed || typeof parsed !== "object") {
    throw new Error("External resolve returned non-JSON response");
  }
  return parsed as ExternalResolveResponse;
}

export async function listAlbums(
  document_id?: number | string,
  limit = 200,
  options?: { includeGlobalAlbums?: boolean }
): Promise<ExternalAlbum[]> {
  const res = await listExternalItems({
    source: "albums",
    limit,
    document_id,
    include_global_albums: options?.includeGlobalAlbums,
  });
  return (res.items ?? []).map((item: any) => ({
    id: String(item?.id ?? ""),
    item_id: Number(item?.item_id ?? 0),
    name: String(item?.name ?? item?.item_name ?? ""),
    item_name: String(item?.item_name ?? ""),
    C_com_group_item_id: item?.C_com_group_item_id,
    C_com_group_item_name: String(item?.C_com_group_item_name ?? ""),
    list_sort: item?.list_sort ?? "",
  }));
}

export async function listResidents(
  document_id?: number | string,
  limit = 5000
): Promise<Array<Record<string, any>>> {
  const res = await listExternalItems({ source: "residents", limit, document_id });
  return (res.items ?? []).map((item: any) => ({
    id: String(item?.id ?? ""),
    item_id: Number(item?.item_id ?? 0),
    first_name: String(item?.first_name ?? ""),
    last_name: String(item?.last_name ?? ""),
    address: String(item?.address ?? ""),
    user_email: String(item?.user_email ?? ""),
    phone_home: String(item?.phone_home ?? ""),
    mobile_phone: String(item?.mobile_phone ?? ""),
    neighborhood_number: String(item?.neighborhood_number ?? ""),
    neighborhood_name: String(item?.neighborhood_name ?? ""),
  }));
}

export async function listNeighborhoods(
  document_id?: number | string,
  limit = 200
): Promise<Array<{ id: string; item_id: number; item_name: string; neighborhood_number: string }>> {
  const res = await listExternalItems({ source: "neighborhoods", limit, document_id });
  return (res.items ?? []).map((item: any) => ({
    id: String(item?.id ?? ""),
    item_id: Number(item?.item_id ?? 0),
    item_name: String(item?.item_name ?? item?.name ?? ""),
    neighborhood_number: String(item?.neighborhood_number ?? ""),
  }));
}

export async function getResident(
  id: string,
  document_id?: number | string
): Promise<ExternalResidentProfile> {
  const res = await getExternalItem({ source: "residents", id, document_id });
  const resident = (res?.value ?? {}) as Record<string, any>;

  return {
    ...resident,
    id: String(resident?.id ?? id ?? ""),
    item_id: Number(resident?.item_id ?? 0),
    display_name: String(resident?.display_name ?? ""),
    first_name: String(resident?.first_name ?? ""),
    last_name: String(resident?.last_name ?? ""),
    address: String(resident?.address ?? ""),
    user_email: String(resident?.user_email ?? ""),
    phone_home: String(resident?.phone_home ?? ""),
    mobile_phone: String(resident?.mobile_phone ?? ""),
    neighborhood_number: String(resident?.neighborhood_number ?? ""),
    neighborhood_name: String(resident?.neighborhood_name ?? ""),
    attachment_filename: String(resident?.attachment_filename ?? ""),
  };
}

export async function listRosterSegments(
  document_id?: number | string,
  limit = 20
): Promise<ExternalRosterSegment[]> {
  const res = await listExternalItems({ source: "roster_segments", limit, document_id });
  return (res.items ?? []).map((item: any) => ({
    id: String(item?.id ?? ""),
    item_id: Number(item?.item_id ?? 0),
    key: String(item?.key ?? ""),
    label: String(item?.label ?? item?.item_name ?? item?.name ?? ""),
    item_name: String(item?.item_name ?? ""),
    list_sort: Number(item?.list_sort ?? 0),
    C_com_group_item_id: Number(item?.C_com_group_item_id ?? 0),
  }));
}

export async function getAlbum(
  id: string,
  document_id?: number | string,
  options?: { includeGlobalAlbums?: boolean }
): Promise<{ album: Record<string, any>; photos: ExternalAlbumPhoto[] }> {
  const res = await getExternalItem({
    source: "albums",
    id,
    document_id,
    include_global_albums: options?.includeGlobalAlbums,
  });
  const album = (res?.value ?? {}) as Record<string, any>;
  const photosRaw = Array.isArray(album?.photos) ? album.photos : [];
  const photos: ExternalAlbumPhoto[] = photosRaw.map((photo: any) => ({
    ...photo,
    id: String(photo?.id ?? ""),
    item_id: Number(photo?.item_id ?? 0),
    item_name: String(photo?.item_name ?? ""),
    attachment_filename: String(photo?.attachment_filename ?? ""),
    url: String(photo?.url ?? ""),
  }));
  return { album, photos };
}